import jsPDF from 'jspdf';
import { Application, CertificateTemplate } from '../types';

/**
 * Loads an image from a URL and returns it as a base64 string or HTMLImageElement
 */
const loadImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = url;
  });
};

export const generateCertificate = async (
  application: Application,
  template: CertificateTemplate
): Promise<jsPDF> => {
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4'
  });

  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();

  try {
    // Load assets
    const [frameImg, logoImg, sealImg, signatureImg] = await Promise.all([
      loadImage('/assets/certificate/frame.png'),
      loadImage('/assets/certificate/logo.png'),
      loadImage('/assets/certificate/seal.png'),
      loadImage('/assets/certificate/signature.png'),
    ]);

    // 1. Draw Frame (Background)
    doc.addImage(frameImg, 'PNG', 0, 0, width, height);

    // 2. Logo - Top Center
    const logoSize = 40;
    doc.addImage(logoImg, 'PNG', (width / 2) - (logoSize / 2), 15, logoSize, logoSize);

    // 3. Organization Name - Below Logo
    doc.setTextColor(10, 80, 160); // Blue
    doc.setFont('times', 'bold');
    doc.setFontSize(28);
    doc.text(template.organizationName.toUpperCase(), width / 2, 62, { align: 'center' });

    // 4. Main Title - "TRAINING COMPLETION CERTIFICATE"
    doc.setTextColor(20, 50, 120);
    doc.setFontSize(22);
    doc.text(template.title.toUpperCase(), width / 2, 75, { align: 'center' });

    // Decorative line
    doc.setDrawColor(200, 160, 40); // Gold-ish
    doc.setLineWidth(0.5);
    doc.line(width / 2 - 50, 82, width / 2 + 50, 82);

    // 5. "This is to certify that"
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(14);
    doc.text('This is to certify that', width / 2, 92, { align: 'center' });

    // 6. Recipient Name - Big and Cursive-style (using Times Bold Italic)
    doc.setTextColor(20, 50, 120);
    doc.setFont('times', 'bolditalic');
    doc.setFontSize(36);
    const fullName = `${application.firstName} ${application.lastName}`;
    doc.text(fullName, width / 2, 110, { align: 'center' });

    // Underline for name
    doc.setDrawColor(200, 160, 40);
    doc.line(width / 2 - 60, 115, width / 2 + 60, 115);

    // 7. Body Text
    doc.setTextColor(80, 80, 80);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(14);
    
    const bodyPart1 = 'has successfully completed the training program on';
    doc.text(bodyPart1, width / 2, 125, { align: 'center' });

    // Course Name - Highlighted
    doc.setTextColor(10, 80, 160);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(`"${template.subTitle}"`, width / 2, 135, { align: 'center' });

    doc.setTextColor(80, 80, 80);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.text(`conducted by ${template.organizationName}.`, width / 2, 143, { align: 'center' });

    // Appreciation Text
    const appreciation = 'We appreciate your dedication, active participation and commitment\ntowards enhancing your skills and knowledge.';
    const splitAppreciation = doc.splitTextToSize(appreciation, width - 80);
    doc.text(splitAppreciation, width / 2, 155, { align: 'center' });

    // 8. Seal - Bottom Center
    const sealSize = 35;
    doc.addImage(sealImg, 'PNG', (width / 2) - (sealSize / 2), height - 55, sealSize, sealSize);

    // 9. Date of Completion - Bottom Left
    const dateY = height - 35;
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Date of Completion', 45, dateY);
    
    doc.setTextColor(20, 50, 120);
    const completionDate = new Date().toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
    doc.text(completionDate, 45, dateY + 7);
    doc.setDrawColor(150, 150, 150);
    doc.line(45, dateY + 9, 85, dateY + 9);

    // 10. Authorized Signatory - Bottom Right
    const sigY = height - 45;
    doc.addImage(signatureImg, 'PNG', width - 85, sigY - 15, 40, 15);
    
    doc.setTextColor(20, 50, 120);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Authorized Signatory', width - 85, sigY + 7);
    
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(template.organizationName, width - 85, sigY + 12);

    // 11. Footer / Certificate ID
    doc.setFontSize(8);
    doc.setTextColor(180, 180, 180);
    const registrationDisplay = application.registrationNumber || application.id.replace(/-/g, '/');
    doc.text(`Certificate ID: ${registrationDisplay.substring(0, 8)}-${Date.now().toString().slice(-4)}`, width - 20, height - 10, { align: 'right' });

  } catch (error) {
    console.error('Error adding images to certificate:', error);
    // Fallback to basic design if images fail to load
    doc.setTextColor(30, 58, 138);
    doc.setFontSize(30);
    doc.text(template.organizationName, width / 2, 50, { align: 'center' });
    doc.setFontSize(20);
    doc.text(template.title, width / 2, 70, { align: 'center' });
    doc.setFontSize(40);
    doc.text(`${application.firstName} ${application.lastName}`, width / 2, 110, { align: 'center' });
  }

  return doc;
};
